/**
 * `errText(e)` — the ONE way this codebase turns a thrown value into text.
 *
 * Before 2026-09-06 the expression
 *   `error instanceof Error ? error.message : String(error)`
 * was pasted 358 times across 116 files (219 when first measured on 08-29,
 * 288 a week later — a helper without a guard regrows), plus a couple of dozen
 * variants with a literal fallback. `String(obj)` on a serialized
 * AppwriteException gives "[object Object]", which is what several toasts
 * showed staff. This helper reads `.message` off plain objects too.
 *
 * Rules, in order:
 *   1. `Error` with a non-empty message → that message.
 *   2. any object with a non-empty string `message` → that message.
 *   3. `fallback`, when given.
 *   4. `String(error)`.
 *
 * Guard: tests/unit/helper-adoption-guards.test.ts ("errText idiom") fails on
 * any new inline copy under components/, app/, hooks/, lib/. This file is the
 * guard's only allow-listed file, because the idiom is quoted above.
 */
export function errText(error: unknown, fallback?: string): string {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message
    if (typeof m === "string" && m) return m
  }
  if (fallback !== undefined) return fallback
  return String(error)
}

/**
 * A structured failure from the `/api/db` proxy (`DatabasesProxy.request`).
 *
 * The proxy answers every refused write with JSON `{ error }` and an HTTP
 * status, and the browser client used to collapse that into a bare
 * `Error(message)` — so a caller could not tell an optimistic-lock 409
 * ("another tablet resolved this first") from a 403 or a 500 without
 * matching on the message text. The status rides on the error instead.
 *
 * `message` is unchanged from before (still the proxy's `error` string), so
 * every existing `errText(err)` / toast path renders exactly what it did.
 */
export class ProxyRequestError extends Error {
  /** HTTP status the proxy answered with. */
  readonly status: number
  /** The proxy's structured error code when it sent one (e.g. 409 on a lock conflict). */
  readonly code: number | undefined

  constructor(message: string, status: number, code?: number) {
    super(message)
    this.name = "ProxyRequestError"
    this.status = status
    this.code = code
  }
}

/**
 * True when a write lost an optimistic-lock race (`expectedUpdatedAt` no
 * longer matches — the proxy answers 409). The right reaction is to reload
 * and let the human look at what the other tablet wrote, never to re-read
 * and retry, which would defeat the lock.
 */
export function isProxyConflictError(error: unknown): boolean {
  return error instanceof ProxyRequestError && error.status === 409
}
